import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useWorkflowNudge from 'hooks/useWorkflowNudge';
import Workflow from 'models/Workflow';
import {
  amendWorkflowAction,
  completeWorkflowAction,
  invokeWorkflowAction,
  markWorkflowActionViewed,
  saveWorkflowActionProgress,
  showWorkflow,
  updateWorkflow,
  UpdateWorkflowInput,
  WorkflowActionIntent,
  workflowQueryKeys,
  WorkflowValuesInput,
} from 'services/api/workflows';
import { useToastActions } from 'stores/toastStore';

// A write's response (create / value-write / complete / rerun / dismiss) now
// reflects the FINAL settled state — the evaluator runs synchronously, including
// re-run cascades (P1-1) — so the acting user's own change renders straight from
// the response.

// The fallback cadence only (OLIO-2602): the WorkflowChannel nudge is the primary
// freshness signal; this slow poll covers dropped sockets and missed events.
const FALLBACK_POLL_INTERVAL_MS = 60_000;

/**
 * The single data hook for a workflow run (the key WEB-0 architectural ask).
 *
 * All fetching lives here so the rest of the UI is agnostic to *how* fresh data
 * arrives. Today it polls `GET /api/workflows/:id` until the run is terminal;
 * swapping to a WebSocket subscription in Phase 2 is a one-file change — only the
 * `useQuery` block below changes, the returned shape stays the same.
 */
export default function useWorkflow(id: string | undefined) {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  const query = useQuery({
    queryKey: workflowQueryKeys.show(id ?? ''),
    queryFn: ({ signal }) => showWorkflow(id!, signal),
    enabled: !!id,
    // --- swap point: replace this background polling with a subscription in Phase 2 ---
    // Background-only: the acting user's writes already return settled state; this
    // catches other users' changes while the run is live, stopping once terminal.
    refetchInterval: (q) => (q.state.data && q.state.data.isTerminal ? false : FALLBACK_POLL_INTERVAL_MS),
    refetchIntervalInBackground: true,
  });

  // Live updates (OLIO-2602): the API broadcasts an id-only nudge whenever this run
  // settles — the shared wiring debounces the burst and invalidates this query, so
  // the fetch stays the single source of truth.
  useWorkflowNudge(id, workflowQueryKeys.show(id ?? ''));

  const onWriteSuccess = (workflow: Workflow) => {
    if (id) queryClient.setQueryData(workflowQueryKeys.show(id), workflow);
    // The list prefix too, not just the show key. A write settles the run, which can move it
    // out of a filtered projection entirely — the Tasks list is `status = action_required`, so
    // the card a person just resolved has to vanish on their own write rather than linger until
    // the broadcast round-trip or the fallback poll.
    queryClient.invalidateQueries({ queryKey: workflowQueryKeys.lists });
  };

  const onWriteError = () => addToast({ text: 'Something went wrong. Please try again' });

  const actionMutation = useMutation({
    mutationFn: ({ actionId, outputs }: { actionId: string; outputs: WorkflowValuesInput }) =>
      completeWorkflowAction({ workflowId: id!, actionId, outputs }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  const amendMutation = useMutation({
    mutationFn: ({ actionId, outputs }: { actionId: string; outputs: WorkflowValuesInput }) =>
      amendWorkflowAction({ workflowId: id!, actionId, outputs }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  // Stages the outputs on the action without completing it (save_progress) — the
  // hand-off flow parks retrieved values here for a person to confirm. `narrationHint` is
  // optional; SexOffenderCheck's Dismiss-All-in-bucket flow uses it to key the activity-log
  // entry off the bucket name rather than the diff-based per-card phrasing.
  const saveProgressMutation = useMutation({
    mutationFn: ({
      actionId,
      outputs,
      narrationHint,
    }: {
      actionId: string;
      outputs: WorkflowValuesInput;
      narrationHint?: string;
    }) => saveWorkflowActionProgress({ workflowId: id!, actionId, outputs, narrationHint }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  // The by-property slideout's commit (OLIO-3022): one request carrying every action the
  // person's single gesture completes. Deliberately NOT a loop over completeAction — the
  // gesture is atomic on the API, and a client-side sequence would let a run come to rest
  // half-answered when the second call failed.
  const submitMutation = useMutation({
    mutationFn: ({ actionCompletions, values }: Pick<UpdateWorkflowInput, 'actionCompletions' | 'values'>) =>
      updateWorkflow({ workflowId: id!, actionCompletions, values }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  const invokeMutation = useMutation({
    mutationFn: ({ actionId, invokeAction }: { actionId: string; invokeAction: WorkflowActionIntent }) =>
      invokeWorkflowAction({ workflowId: id!, actionId, invokeAction }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  // Viewing an action records the view AND completes it (action_required → completed). Unlike the
  // other writes, the endpoint returns ONLY the view attribution — not the settled workflow — so
  // we must NOT write the response into the cache (a partial payload would blank the page). Instead
  // we refetch to pick up the stamp + settle authoritatively. Silent on error — a failed view-stamp
  // is a side effect of opening the drawer and shouldn't interrupt reviewing.
  const markViewedMutation = useMutation({
    mutationFn: ({ actionId }: { actionId: string }) => markWorkflowActionViewed({ workflowId: id!, actionId }),
    onSuccess: () => {
      if (id) queryClient.invalidateQueries({ queryKey: workflowQueryKeys.show(id) });
    },
  });

  return {
    workflow: query.data,
    isLoading: query.isPending && !!id,
    isError: query.isError,
    // True while a poll or write is in flight — drives a subtle "updating" affordance.
    isFetching: query.isFetching,
    refetch: query.refetch,
    completeAction: actionMutation.mutate,
    // Awaitable variant — bulk facility decisions run sequentially so out-of-order
    // responses can't clobber the cache with an earlier snapshot.
    completeActionAsync: actionMutation.mutateAsync,
    isCompletingAction: actionMutation.isPending,
    amendAction: amendMutation.mutate,
    amendActionAsync: amendMutation.mutateAsync,
    isAmendingAction: amendMutation.isPending,
    saveActionProgress: saveProgressMutation.mutate,
    isSavingActionProgress: saveProgressMutation.isPending,
    submitActionCompletions: submitMutation.mutate,
    isSubmittingCompletions: submitMutation.isPending,
    invokeAction: invokeMutation.mutate,
    isInvokingAction: invokeMutation.isPending,
    markActionViewed: markViewedMutation.mutate,
  };
}
